// Coordinate math for Form Setup placeholders.
//
// Placeholders are stored in PIXELS OF THE SOURCE IMAGE, origin top-left — the
// same numbers `form_setup/helpers.py::fill_placeholders` hands to PIL's
// `draw.text(xy=node['coordinates'])`. The editor lays them out over an <img>
// rendered at a fixed CSS size, so every read divides by the ratio between the
// two and every write multiplies back.
//
// Django rasterises the uploaded PDF with pdf2image at its default 200 DPI
// (form_setup/serializers.py), so a US-Letter page lands at roughly 1700x2200
// px against a 612x792 view — a true ratio of 200/72 = 2.7778, not the 2.7 that
// was hard-coded here. That 2.8% error put text ~47px left of where the editor
// showed it at the right-hand edge of the page, and it was simply wrong for a
// directly-uploaded PNG/JPG of any other size.
//
// So: derive the ratio from the image's own natural size whenever we have it,
// and fall back to the legacy constant only until it loads.

// US Letter at 72dpi — the CSS size the form image is rendered at.
export const VIEW_WIDTH = 612;
export const VIEW_HEIGHT = 792;

// What the code used before the ratio was derived. Kept as the pre-load
// fallback so a slow image cannot make placeholders jump.
export const LEGACY_SCALE = 2.7;

// What Django rasterises uploads at (pdf2image's default, form_setup/
// serializers.py). Mirrored by RASTER_DPI in form_setup/pdf_render.py.
export const RASTER_DPI = 200;

// A PDF point is 1/72 inch.
const POINTS_PER_INCH = 72;

/**
 * Image pixels per view pixel, from the loaded image's natural width.
 * Falls back to LEGACY_SCALE for a missing/zero width (image not loaded yet).
 */
export const scaleFromNaturalWidth = (naturalWidth?: number): number => {
  if (!naturalWidth || !Number.isFinite(naturalWidth) || naturalWidth <= 0) {
    return LEGACY_SCALE;
  }
  return naturalWidth / VIEW_WIDTH;
};

/**
 * Image pixels per view pixel for a PDF being rendered by react-pdf.
 *
 * The Add Form canvas draws the page to a <canvas>, not an <img>, so there is
 * no naturalWidth to measure and the scale would otherwise sit on the legacy
 * 2.7 fallback forever — placing every new form's placeholders ~2.8% off on
 * Letter. The page's own width in points gives the answer exactly, and for any
 * paper size: A4 legitimately lands near 2.70 while Letter lands on 2.7778.
 */
export const scaleFromPdfPageWidth = (widthPt?: number): number => {
  if (!widthPt || !Number.isFinite(widthPt) || widthPt <= 0) {
    return LEGACY_SCALE;
  }
  return scaleFromNaturalWidth((widthPt * RASTER_DPI) / POINTS_PER_INCH);
};

/**
 * Printed point size of a stored font size.
 *
 * The Font Size select is in pixels of the 200-DPI raster, not points — the
 * server divides it by the same ratio it divides the coordinates by
 * (`pdf_render.py::_instruction_html`: `size = fontsize / scale_y`). So a
 * form set to 24 prints at ~8.6pt.
 *
 * The view is 72dpi, where one pixel IS one point, so this doubles as the
 * on-screen size the editor must draw a label at for it to sit where it will
 * print.
 */
export const fontPxToPt = (fontPx: any, scale: number): number => {
  const n = Number(fontPx);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n / (scale || LEGACY_SCALE);
};

/**
 * Line box height as a multiple of the font size.
 *
 * Mirrors LINE_HEIGHT in form_setup/pdf_render.py, which is both the overlay
 * stylesheet's `line-height` and the multiplier the renderer sizes a wrapped
 * placeholder's box with. The two have to be the same number or the box drawn
 * in the editor is not the box that prints.
 */
export const LINE_HEIGHT = 1.15;

/**
 * On-screen height, in view pixels, of a wrapped placeholder.
 *
 * The renderer gives a wrapped value exactly `font_size * LINE_HEIGHT *
 * max_lines` points of vertical room and clips anything past it
 * (pdf_render.py::_instruction_html). The canvas is 72dpi, so a view pixel IS
 * a point and the same product is the height to draw — which is what lets the
 * chip show the real footprint instead of whatever height it was dragged to.
 *
 * `fontPt` is the PRINTED size (fontPxToPt), not the raster px in the picker.
 */
export const wrappedBoxHeight = (
  fontPt: number,
  maxLines: number,
): number => {
  const size = Number(fontPt);
  const lines = Number(maxLines);
  if (!Number.isFinite(size) || size <= 0) return 0;
  if (!Number.isFinite(lines) || lines <= 0) return 0;
  return Math.round(size * LINE_HEIGHT * lines);
};

/** Stored image pixels -> on-screen view pixels. */
export const imageToView = (value: any, scale: number): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n / (scale || LEGACY_SCALE));
};

/** On-screen view pixels -> stored image pixels. */
export const viewToImage = (value: any, scale: number): number => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * (scale || LEGACY_SCALE));
};
